import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PropertyActions - Barre d'actions pour les biens sélectionnés
 * 
 * @param {array} selectedProperties - IDs des biens sélectionnés
 * @param {object} userProfile - Profil utilisateur
 * @param {string} currentFilter - Filtre actuel ('shared', 'active', etc.)
 * @param {array} properties - Liste des propriétés (pour vérifier is_public)
 * @param {function} onShareSuccess - Callback quand le partage réussit
 * @param {function} onUnshareSuccess - Callback quand le dé-partage réussit
 */
const PropertyActions = ({ 
  selectedProperties = [], 
  userProfile,
  currentFilter,
  properties = [],
  onShareSuccess,
  onUnshareSuccess
}) => {
  const [shareLoading, setShareLoading] = useState(false);

  // TEST: Vérifier que le composant se rend
  if (typeof window !== 'undefined') {
    window.PROPERTY_ACTIONS_RENDERED = true;
    window.PROPERTY_ACTIONS_COUNT = selectedProperties.length;
  }

  console.log('🎬 PropertyActions render:', { 
    selectedCount: selectedProperties.length,
    ids: selectedProperties 
  });

  const handlePDFClick = () => {
    console.log('📄 [PropertyActions] Clic PDF, biens:', selectedProperties);
    alert('Fonction PDF groupée en cours d\'implémentation.\nUtilisez le menu (⋮) sur chaque bien pour l\'instant.');
  };

  // Vérifier si on est sur l'onglet "Partagés" ET si des biens partagés sont sélectionnés
  const sharedInSelection = properties.filter(p => 
    selectedProperties.includes(p.id) && p.is_public && p.public_code
  ).length;

  const isOnSharedTab = currentFilter === 'shared';
  const shouldShowUnshare = isOnSharedTab && sharedInSelection > 0;

  const handleShareClick = async () => {
    // Si on est sur l'onglet "Partagés", désactiver le partage
    if (shouldShowUnshare) {
      console.log('🔓 [PropertyActions] Désactivation du partage...');
      
      if (window.confirm(`Êtes-vous sûr de vouloir désactiver le partage de ${sharedInSelection} bien(s) ?\n\nLes liens publics ne seront plus accessibles.`)) {
        setShareLoading(true);
        try {
          const sharedProps = properties.filter(p => 
            selectedProperties.includes(p.id) && p.is_public && p.public_code
          );
          
          for (const property of sharedProps) {
            await axios.delete(`${API}/properties/${property.id}/public-link`);
          }
          
          if (onUnshareSuccess) {
            onUnshareSuccess();
          }
          
          alert(`Partage désactivé pour ${sharedInSelection} bien(s)`);
        } catch (error) {
          console.error('❌ [PropertyActions] Erreur désactivation:', error);
          alert('❌ Erreur lors de la désactivation du partage: ' + (error.response?.data?.detail || error.message));
        } finally {
          setShareLoading(false);
        }
      }
      return;
    }

    // Sinon, activer le partage (comportement normal)
    console.log('🖱️ [PropertyActions] Clic Partager DIRECT !');
    console.log('🔍 [PropertyActions] selectedProperties:', selectedProperties);
    console.log('🔍 [PropertyActions] Nombre:', selectedProperties.length);
    console.log('🔍 [PropertyActions] Type:', typeof selectedProperties, Array.isArray(selectedProperties));
    console.log('🔍 [PropertyActions] Détails complets:', JSON.stringify(selectedProperties, null, 2));
    
    if (selectedProperties.length === 0) {
      console.log('⚠️ [PropertyActions] Aucun bien sélectionné');
      alert('Veuillez sélectionner au moins un bien');
      return;
    }

    console.log('✅ [PropertyActions] Début création lien...');
    setShareLoading(true);

    try {
      const payload = {
        property_ids: selectedProperties,
        title: `Collection de ${selectedProperties.length} bien(s)`,
        created_by: userProfile?.id || 'system'
      };
      console.log('📤 [PropertyActions] Payload AVANT envoi:', JSON.stringify(payload, null, 2));

      const response = await axios.post(`${API}/shared-collections`, payload);
      console.log('📥 [PropertyActions] Réponse API complète:', JSON.stringify(response.data, null, 2));

      const fullUrl = `${window.location.origin}/shared/${response.data.share_token}`;
      console.log('🔗 [PropertyActions] URL complète:', fullUrl);

      if (onShareSuccess) {
        console.log('✅ [PropertyActions] Appel callback onShareSuccess');
        onShareSuccess(fullUrl);
      }

      console.log('✅ [PropertyActions] Partage terminé avec succès !');
    } catch (error) {
      console.error('❌ [PropertyActions] Erreur:', error);
      console.error('❌ [PropertyActions] Erreur détails:', error.response?.data);
      alert('❌ Erreur lors de la création du lien de partage: ' + (error.response?.data?.detail || error.message));
    } finally {
      setShareLoading(false);
      console.log('🏁 [PropertyActions] shareLoading = false');
    }
  };

  const handleEmailClick = () => {
    console.log('📧 [PropertyActions] Clic Email, biens:', selectedProperties);
    alert('Fonction Email groupée en cours d\'implémentation.');
  };

  return (
    <>
      {/* Bouton PDF - Style mockup : fond blanc, bordure */}
      <button 
        data-testid="pdf-button-main"
        onClick={handlePDFClick}
        className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={selectedProperties.length === 0}
        title="Générer PDF des fiches biens"
      >
        <i className="bi bi-file-pdf"></i>
        <span>PDF</span>
      </button>
      
      {/* Bouton Partager / Désactiver le partage - Contextuel selon l'onglet */}
      <button 
        data-testid="share-button-actions"
        onClick={handleShareClick}
        className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={selectedProperties.length === 0 || shareLoading}
        title={shouldShowUnshare ? "Désactiver le partage des biens sélectionnés" : "Générer lien(s) de partage public"}
      >
        <i className={`bi ${shareLoading ? 'bi-hourglass-split' : shouldShowUnshare ? 'bi-link-45deg-slash' : 'bi-share'}`}></i>
        <span>
          {shareLoading 
            ? 'Chargement...' 
            : shouldShowUnshare 
              ? 'Désactiver le partage' 
              : 'Partager'
          }
        </span>
      </button>
      
      {/* Bouton Email - Style mockup : fond BLANC (pas vert!), bordure */}
      <button 
        data-testid="email-button-main"
        onClick={handleEmailClick}
        className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={selectedProperties.length === 0}
        title="Envoyer par email aux clients"
      >
        <i className="bi bi-envelope"></i>
        <span>Email</span>
      </button>
    </>
  );
};

export default PropertyActions;
